import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import { environment } from '../../../environments/environment';
import {AdminApiService} from '../../admin-api.service';
import {AdminDiscordLinkedServer, AdminDiscordProfile, AdminDiscordStats, AdminLinkedAccount, AdminSpotifyNowPlaying, AdminTwitchChatbotDiagnostics, AdminUser, AuditEvent, SupportSession} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {auditActionLabel} from '../../shared/audit-action-labels';
import {UiBadgeComponent, UiButtonComponent, UiCardComponent, UiIconComponent, UiInputComponent, UiTabComponent, UiToastService, UiToggleComponent} from 'freyr-ui';

interface RecentAuditRow {
  id: string;
  type: 'event' | 'support-group';
  event?: AuditEvent;
  supportSessionId?: string;
  events: AuditEvent[];
}

/** Clés alignées sur les entrées de délégation support côté API (ordre = ordre d’affichage courant). */
const SUPPORT_DELEGATION_PERMISSION_KEYS = [
  'AUTO_MESSAGES_READ',
  'AUTO_MESSAGES_WRITE',
  'BOT_COMMANDS_READ',
  'BOT_COMMANDS_WRITE',
  'DISCORD_READ',
  'DISCORD_WRITE',
  'MODERATION_READ',
  'MODERATION_WRITE',
  'TWITCH_DATA_READ',
  'TWITCH_SCHEDULE_WRITE',
  'LEVELS_READ',
  'LEVELS_WRITE'
] as const;

@Component({
  selector: 'app-users-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, UiBadgeComponent, UiButtonComponent, UiInputComponent, UiCardComponent, UiIconComponent, UiTabComponent, UiToggleComponent, AdminTranslatePipe],
  templateUrl: './users-page.component.html',
  styleUrl: './users-page.component.scss'
})
export class UsersPageComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);

  readonly availableRoles = [
    { key: 'USER', labelKey: 'admin.roles.USER' },
    { key: 'BETA_TESTER', labelKey: 'admin.roles.BETA_TESTER' },
    { key: 'SUPPORT', labelKey: 'admin.roles.SUPPORT' },
    { key: 'ADMIN', labelKey: 'admin.roles.ADMIN' }
  ];

  readonly supportPermissions = SUPPORT_DELEGATION_PERMISSION_KEYS.map(key => ({
    key,
    labelKey: `admin.permissions.${key}` as const
  }));

  readonly integrationProviders = [
    { key: 'DISCORD', labelKey: 'admin.users.providers.DISCORD', icon: 'discord' as const },
    { key: 'SPOTIFY', labelKey: 'admin.users.providers.SPOTIFY', icon: 'spotify' as const },
    { key: 'YOUTUBE', labelKey: 'admin.users.providers.YOUTUBE', icon: 'youtube' as const }
  ];

  currentUser: AdminUser | null = null;
  users: AdminUser[] = [];
  userSearch = '';
  supportReason = '';
  roleChangeReason = '';
  selectedPermissions: string[] = [...SUPPORT_DELEGATION_PERMISSION_KEYS];
  selectedUserRoleDraft: string[] = [];
  selectedUser: AdminUser | null = null;
  linkedAccounts: AdminLinkedAccount[] = [];
  discordProfile: AdminDiscordProfile | null = null;
  discordLinkedServers: AdminDiscordLinkedServer[] = [];
  discordStats: AdminDiscordStats | null = null;
  spotifyNowPlaying: AdminSpotifyNowPlaying | null = null;
  twitchChatbotDiagnostic: AdminTwitchChatbotDiagnostics | null = null;
  recentAuditEvents: AuditEvent[] = [];
  activeSupportSession: SupportSession | null = null;
  loadingUsers = false;
  loadingUserDetails = false;
  loadingActiveSupportSession = false;
  loadingTwitchChatbotDiagnostic = false;
  launchingSupport = false;
  resumingSupportSessionId: string | null = null;
  supportModalOpen = false;
  savingRoles = false;
  activeIntegrationTab: 'DISCORD' | 'SPOTIFY' | 'YOUTUBE' = 'DISCORD';
  readonly recentAuditPageSize = 5;
  recentAuditPage = 1;
  readonly webAppBaseUrl = (environment.webAppUrl ?? '').replace(/\/+$/, '');

  ngOnInit(): void {
    this.api.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser = user;
      }
    });

    this.route.paramMap.subscribe(params => {
      this.loadUsers(params.get('id'));
    });
  }

  get canEditUserRoles(): boolean {
    return this.currentUser?.roles?.includes('ADMIN') ?? false;
  }

  get isCurrentUserAdmin(): boolean {
    return this.currentUser?.roles?.includes('ADMIN') ?? false;
  }

  get filteredUsers(): AdminUser[] {
    const query = this.userSearch.trim().toLowerCase();
    if (!query) {
      return this.users;
    }
    return this.users.filter(user =>
      (user.username ?? '').toLowerCase().includes(query)
      || (user.email ?? '').toLowerCase().includes(query)
      || (user.id ?? '').toLowerCase().includes(query)
    );
  }

  reloadUsers(): void {
    this.loadUsers(this.selectedUser?.id ?? this.route.snapshot.paramMap.get('id'));
  }

  openUserDashboardPreview(): void {
    const id = this.selectedUser?.id;
    if (!id || !this.webAppBaseUrl) {
      return;
    }
    // freyr-web utilise withHashLocation() : le query doit suivre le fragment (#/…),
    // sinon Angular ne voit pas adminPreviewUserId et la bannière prévisualisation ne s’affiche pas.
    const url = `${this.webAppBaseUrl}/#/dashboard?adminPreviewUserId=${encodeURIComponent(id)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  loadUsers(selectedUserId?: string | null): void {
    this.loadingUsers = true;
    this.api.listUsers().subscribe({
      next: (users) => {
        this.users = users ?? [];
        const targetId = selectedUserId || this.selectedUser?.id;
        if (targetId) {
          const matchedUser = this.users.find(user => user.id === targetId) ?? null;
          this.selectedUser = matchedUser;
          if (matchedUser) {
            this.resetRoleDraft();
            this.loadSelectedUserDetails();
          }
        }
        this.loadingUsers = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loadingUsers = false;
        this.handleError(error, 'admin.users.loadError');
      }
    });
  }

  openUser(user: AdminUser): void {
    void this.router.navigate(['/users', user.id]);
  }

  resetRoleDraft(): void {
    this.selectedUserRoleDraft = [...(this.selectedUser?.roles ?? ['USER'])];
  }

  isRoleSelected(role: string): boolean {
    return this.selectedUserRoleDraft.includes(role);
  }

  toggleRole(role: string): void {
    if (this.isProtectedSelfAdminRole(role)) {
      return;
    }
    const index = this.selectedUserRoleDraft.indexOf(role);
    if (index >= 0) {
      this.selectedUserRoleDraft.splice(index, 1);
      if (this.selectedUserRoleDraft.length === 0) {
        this.selectedUserRoleDraft = ['USER'];
      }
      return;
    }
    this.selectedUserRoleDraft.push(role);
  }

  isProtectedSelfAdminRole(role: string): boolean {
    return role === 'ADMIN'
      && this.currentUser?.id === this.selectedUser?.id
      && this.selectedUserRoleDraft.includes('ADMIN');
  }

  saveRoles(): void {
    if (!this.selectedUser?.id) {
      this.toast.error(this.i18n.t('admin.users.selectUserError'));
      return;
    }

    this.savingRoles = true;

    this.api.updateUserRoles(this.selectedUser.id, this.selectedUserRoleDraft, this.roleChangeReason).subscribe({
      next: (updatedUser) => {
        this.savingRoles = false;
        this.replaceUser(updatedUser);
        this.selectedUser = updatedUser;
        this.resetRoleDraft();
        this.toast.success(this.i18n.t('admin.users.rolesUpdated', { username: updatedUser.username }));
        if (this.currentUser?.id === updatedUser.id) {
          this.currentUser = updatedUser;
        }
        this.loadSelectedUserDetails();
      },
      error: (error: HttpErrorResponse) => {
        this.savingRoles = false;
        this.handleError(error, 'admin.users.updateRolesError');
      }
    });
  }

  isPermissionSelected(permission: string): boolean {
    return this.selectedPermissions.includes(permission);
  }

  togglePermission(permission: string): void {
    const index = this.selectedPermissions.indexOf(permission);
    if (index >= 0) {
      this.selectedPermissions.splice(index, 1);
      if (this.selectedPermissions.length === 0) {
        this.selectedPermissions = this.sortPermissions([...SUPPORT_DELEGATION_PERMISSION_KEYS]);
      }
      return;
    }
    this.selectedPermissions.push(permission);
    this.selectedPermissions = this.sortPermissions(this.selectedPermissions);
  }

  launchSupportSession(): void {
    if (!this.selectedUser?.id) {
      this.toast.error(this.i18n.t('admin.users.selectUserError'));
      return;
    }
    if (!this.supportReason.trim()) {
      this.toast.error(this.i18n.t('admin.users.supportReasonRequired'));
      return;
    }

    this.launchingSupport = true;

    this.api.createSupportSession({
      targetUserId: this.selectedUser.id,
      permissions: this.sortPermissions(this.selectedPermissions),
      reason: this.supportReason.trim()
    }).subscribe({
      next: (response) => {
        this.launchingSupport = false;
        this.toast.success(this.i18n.t('admin.users.supportOpened', { username: this.selectedUser?.username ?? '' }));
        if (response?.redirectUrl) {
          const popup = window.open(response.redirectUrl, '_blank', 'noopener,noreferrer');
          if (!popup) {
            window.location.href = response.redirectUrl;
          }
        }
        this.closeSupportModal();
      },
      error: (error: HttpErrorResponse) => {
        this.launchingSupport = false;
        this.handleError(error, 'admin.users.supportOpenError');
      }
    });
  }

  openSupportModal(): void {
    if (!this.selectedUser) {
      return;
    }
    this.selectedPermissions = this.sortPermissions([...SUPPORT_DELEGATION_PERMISSION_KEYS]);
    this.supportModalOpen = true;
  }

  closeSupportModal(): void {
    if (this.launchingSupport) {
      return;
    }
    this.supportModalOpen = false;
  }

  openAuditForSelectedUser(): void {
    if (!this.selectedUser?.id) {
      return;
    }
    void this.router.navigate(['/audit'], {
      queryParams: {
        effectiveUserId: this.selectedUser.id
      }
    });
  }

  loadSelectedUserDetails(): void {
    if (!this.selectedUser?.id) {
      this.linkedAccounts = [];
      this.discordProfile = null;
      this.discordLinkedServers = [];
      this.discordStats = null;
      this.spotifyNowPlaying = null;
      this.twitchChatbotDiagnostic = null;
      this.recentAuditEvents = [];
      this.activeSupportSession = null;
      this.recentAuditPage = 1;
      return;
    }

    this.loadingUserDetails = true;
    this.loadingActiveSupportSession = true;
    this.api.getUserDetails(this.selectedUser.id).subscribe({
      next: (details) => {
        this.selectedUser = details.user;
        this.replaceUser(details.user);
        this.linkedAccounts = details.linkedAccounts ?? [];
        this.discordProfile = details.discordProfile ?? null;
        this.discordLinkedServers = details.discordLinkedServers ?? [];
        this.discordStats = details.discordStats ?? null;
        this.spotifyNowPlaying = details.spotifyNowPlaying ?? null;
        this.ensureActiveIntegrationTab();
        this.recentAuditEvents = details.recentAuditEvents ?? [];
        this.recentAuditPage = 1;
        this.loadingUserDetails = false;
        this.loadActiveSupportSession(details.user.id);
        this.loadTwitchChatbotDiagnostic(details.user.id);
      },
      error: (error: HttpErrorResponse) => {
        this.loadingUserDetails = false;
        this.loadingActiveSupportSession = false;
        this.loadingTwitchChatbotDiagnostic = false;
        this.handleError(error, 'admin.users.loadingDetailsError');
      }
    });
  }

  chatbotLevel(entry: AdminTwitchChatbotDiagnostics | null): 'ready' | 'degraded' | 'blocked' {
    switch ((entry?.complianceState || 'UNKNOWN').toUpperCase()) {
      case 'CHATBOT_READY':
        return 'ready';
      case 'EVENTSUB_OK_IRC_FALLBACK_ENABLED':
      case 'EVENTSUB_OK_USER_TOKEN_FALLBACK':
        return 'degraded';
      default:
        return 'blocked';
    }
  }

  chatbotLevelBadgeClass(entry: AdminTwitchChatbotDiagnostics | null): string {
    switch (this.chatbotLevel(entry)) {
      case 'ready':
        return 'status-badge status-badge--success';
      case 'degraded':
        return 'status-badge status-badge--warning';
      default:
        return 'status-badge status-badge--error';
    }
  }

  chatbotLevelLabelKey(entry: AdminTwitchChatbotDiagnostics | null): string {
    return `admin.twitchBots.chatRouting.level.${this.chatbotLevel(entry)}`;
  }

  complianceStatusKey(entry: AdminTwitchChatbotDiagnostics | null): string {
    return `admin.twitchBots.chatRouting.status.${(entry?.complianceState || 'UNKNOWN').toUpperCase()}`;
  }

  complianceDescriptionKey(entry: AdminTwitchChatbotDiagnostics | null): string {
    return `admin.twitchBots.chatRouting.description.${(entry?.complianceState || 'UNKNOWN').toUpperCase()}`;
  }

  deliveryModeKey(entry: AdminTwitchChatbotDiagnostics | null): string {
    return `admin.twitchBots.chatRouting.deliveryMode.${(entry?.deliveryTokenMode || 'MISSING').toUpperCase()}`;
  }

  canResumeSupportSession(session: SupportSession | null | undefined): boolean {
    if (!session || session.status !== 'ACTIVE') {
      return false;
    }
    return session.actorUserId === this.currentUser?.id || this.isCurrentUserAdmin;
  }

  activeSupportSessionHint(session: SupportSession | null | undefined): string | null {
    if (!session) {
      return null;
    }
    if (session.actorUserId === this.currentUser?.id) {
      return this.i18n.t('admin.users.activeSupportOwned');
    }
    if (this.isCurrentUserAdmin) {
      return this.i18n.t('admin.users.activeSupportAdminOverride', {
        username: session.actorUsername || session.actorUserId
      });
    }
    return this.i18n.t('admin.users.activeSupportRestricted', {
      username: session.actorUsername || session.actorUserId
    });
  }

  resumeActiveSupportSession(): void {
    if (!this.activeSupportSession || !this.canResumeSupportSession(this.activeSupportSession)) {
      return;
    }

    this.resumingSupportSessionId = this.activeSupportSession.id;
    this.api.resumeSupportSession(this.activeSupportSession.id).subscribe({
      next: (response) => {
        this.resumingSupportSessionId = null;
        if (response?.redirectUrl) {
          const popup = window.open(response.redirectUrl, '_blank', 'noopener,noreferrer');
          if (!popup) {
            window.location.href = response.redirectUrl;
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.resumingSupportSessionId = null;
        this.handleError(error, 'admin.support.resumeError');
      }
    });
  }

  expiresLabel(date: string | null | undefined): string {
    return date
      ? this.i18n.t('admin.users.expiresAt', { date: new Date(date).toLocaleString(this.i18n.currentLang === 'en' ? 'en-GB' : 'fr-FR') })
      : this.i18n.t('admin.users.noKnownExpiry');
  }

  roleDescription(role: string): string {
    const isEnglish = this.i18n.currentLang === 'en';
    switch (role) {
      case 'BETA_TESTER':
        return isEnglish ? 'Early access to upcoming features.' : 'Acces anticipe aux fonctionnalites a venir.';
      case 'SUPPORT':
        return isEnglish ? 'Guided support intervention access.' : 'Intervention support et assistance encadree.';
      case 'ADMIN':
        return isEnglish ? 'Full backoffice access.' : 'Acces complet au backoffice.';
      case 'USER':
      default:
        return isEnglish ? 'Standard account access.' : 'Acces standard au compte.';
    }
  }

  permissionDescription(permission: string): string {
    const isEnglish = this.i18n.currentLang === 'en';
    switch (permission) {
      case 'AUTO_MESSAGES_READ':
        return isEnglish ? 'View configuration and statuses.' : 'Consulter la configuration et les statuts.';
      case 'AUTO_MESSAGES_WRITE':
        return isEnglish ? 'Create, update, and delete.' : 'Creer, modifier et supprimer.';
      case 'BOT_COMMANDS_READ':
        return isEnglish ? 'View the configuration.' : 'Consulter la configuration.';
      case 'BOT_COMMANDS_WRITE':
        return isEnglish ? 'Create, update, and delete.' : 'Creer, modifier et supprimer.';
      case 'DISCORD_READ':
        return isEnglish ? 'View servers and configuration.' : 'Consulter les serveurs et la configuration.';
      case 'DISCORD_WRITE':
        return isEnglish ? 'Edit settings and publications.' : this.i18n.t('admin.users.integrationDescriptions.DISCORD');
      case 'MODERATION_READ':
        return isEnglish ? 'Review active rules.' : 'Voir les regles actives.';
      case 'MODERATION_WRITE':
        return isEnglish ? 'Update automatic protection.' : 'Mettre a jour la protection automatique.';
      case 'TWITCH_DATA_READ':
        return isEnglish ? 'Read planning, stats, and metadata.' : 'Lire le planning, les stats et les metadonnees.';
      case 'TWITCH_SCHEDULE_WRITE':
        return isEnglish ? 'Create and edit schedule slots.' : 'Creer et modifier les creneaux.';
      case 'LEVELS_READ':
        return isEnglish ? 'View levels settings and panel.' : 'Consulter les reglages de niveaux et le panel.';
      case 'LEVELS_WRITE':
        return isEnglish ? 'Edit levels settings and panel.' : 'Modifier les reglages de niveaux et le panel.';
      default:
        return '';
    }
  }

  actionLabel(actionType: string): string {
    return auditActionLabel(actionType, this.i18n.currentLang);
  }

  recentAuditRowLabel(row: RecentAuditRow): string {
    if (row.type === 'support-group') {
      return row.events.length > 1
        ? `Session support (${row.events.length} evenements)`
        : 'Session support';
    }
    return this.actionLabel(row.event?.actionType ?? '');
  }

  recentAuditRowReason(row: RecentAuditRow): string {
    if (row.type === 'support-group') {
      const firstReason = row.events.find(event => !!event.reason?.trim())?.reason?.trim();
      return firstReason || this.i18n.t('admin.audit.noReason');
    }
    return row.event?.reason || this.i18n.t('admin.audit.noReason');
  }

  recentAuditRowDate(row: RecentAuditRow): string {
    return row.events[0]?.occurredAt ?? row.event?.occurredAt ?? '';
  }

  recentAuditRowSuccess(row: RecentAuditRow): boolean {
    return row.events.every(event => event.success);
  }

  integrationAccount(provider: string): AdminLinkedAccount | null {
    return this.linkedAccounts.find(account => (account.provider ?? '').toUpperCase() === provider.toUpperCase()) ?? null;
  }

  integrationLinked(provider: string): boolean {
    return !!this.integrationAccount(provider)?.linked;
  }

  setActiveIntegrationTab(provider: string): void {
    this.activeIntegrationTab = provider as 'DISCORD' | 'SPOTIFY' | 'YOUTUBE';
  }

  integrationStatusTitle(provider: string): string {
    const label = this.integrationProviderLabel(provider);
    return this.integrationLinked(provider) ? `${label} connecte` : `${label} non connecte`;
  }

  integrationProviderLabel(provider: string): string {
    return this.i18n.t(this.integrationProviders.find(item => item.key === provider)?.labelKey ?? provider);
  }

  integrationDescription(provider: string): string {
    switch (provider) {
      case 'DISCORD':
        return this.i18n.t('admin.users.integrationDescriptions.DISCORD');
      case 'SPOTIFY':
        return this.i18n.t('admin.users.integrationDescriptions.SPOTIFY');
      case 'YOUTUBE':
        return this.i18n.t('admin.users.integrationDescriptions.YOUTUBE');
      default:
        return '';
    }
  }

  integrationLastUpdate(account: AdminLinkedAccount | null): string | null {
    if (!account?.updatedAt) {
      return null;
    }
    return new Date(account.updatedAt).toLocaleString(this.i18n.currentLang === 'en' ? 'en-GB' : 'fr-FR');
  }

  get shouldAnimateDiscordServers(): boolean {
    return this.discordLinkedServers.length > 2;
  }

  get paginatedRecentAuditRows(): RecentAuditRow[] {
    const rows: RecentAuditRow[] = [];
    const supportGroups = new Map<string, AuditEvent[]>();

    for (const event of this.recentAuditEvents) {
      const supportSessionId = event.supportSessionId?.trim();
      if (!supportSessionId) {
        rows.push({
          id: event.id,
          type: 'event',
          event,
          events: [event]
        });
        continue;
      }

      const group = supportGroups.get(supportSessionId) ?? [];
      group.push(event);
      supportGroups.set(supportSessionId, group);
    }

    for (const [supportSessionId, events] of supportGroups.entries()) {
      rows.push({
        id: `support-${supportSessionId}`,
        type: 'support-group',
        supportSessionId,
        events
      });
    }

    rows.sort((left, right) => {
      const leftDate = left.events[0]?.occurredAt ?? '';
      const rightDate = right.events[0]?.occurredAt ?? '';
      return rightDate.localeCompare(leftDate);
    });

    const start = (this.recentAuditPage - 1) * this.recentAuditPageSize;
    return rows.slice(start, start + this.recentAuditPageSize);
  }

  get recentAuditRowCount(): number {
    const supportSessionIds = new Set(
      this.recentAuditEvents
        .map(event => event.supportSessionId?.trim())
        .filter((value): value is string => !!value)
    );
    const standaloneEvents = this.recentAuditEvents.filter(event => !event.supportSessionId?.trim()).length;
    return standaloneEvents + supportSessionIds.size;
  }

  get recentAuditTotalPages(): number {
    return Math.max(1, Math.ceil(this.recentAuditRowCount / this.recentAuditPageSize));
  }

  previousRecentAuditPage(): void {
    this.recentAuditPage = Math.max(1, this.recentAuditPage - 1);
  }

  nextRecentAuditPage(): void {
    this.recentAuditPage = Math.min(this.recentAuditTotalPages, this.recentAuditPage + 1);
  }

  rolesSummary(roles: string[] | null | undefined): string {
    const values = roles?.length ? roles : ['USER'];
    return values.map(role => this.i18n.t(`admin.roles.${role}`)).join(' • ');
  }

  private replaceUser(user: AdminUser): void {
    const index = this.users.findIndex(existing => existing.id === user.id);
    if (index >= 0) {
      this.users = [
        ...this.users.slice(0, index),
        user,
        ...this.users.slice(index + 1)
      ];
      return;
    }
    this.users = [user, ...this.users];
  }

  private handleError(error: HttpErrorResponse, fallbackKey: string): void {
    const message = typeof error.error?.message === 'string' && error.error.message.trim()
      ? error.error.message.trim()
      : this.i18n.t(fallbackKey);
    this.toast.error(message);
  }

  private loadActiveSupportSession(userId: string): void {
    this.api.listSupportSessions('ACTIVE').subscribe({
      next: (sessions) => {
        this.activeSupportSession = this.pickMostRecentActiveSessionForUser(sessions ?? [], userId);
        this.loadingActiveSupportSession = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loadingActiveSupportSession = false;
        this.activeSupportSession = null;
        this.handleError(error, 'admin.support.loadError');
      }
    });
  }

  private loadTwitchChatbotDiagnostic(userId: string): void {
    this.loadingTwitchChatbotDiagnostic = true;
    this.api.listTwitchChatbotDiagnostics().subscribe({
      next: (entries) => {
        const diagnostics = Array.isArray(entries) ? entries : [];
        this.twitchChatbotDiagnostic = diagnostics.find(entry => entry.freyrUserId === userId) ?? null;
        this.loadingTwitchChatbotDiagnostic = false;
      },
      error: () => {
        this.twitchChatbotDiagnostic = null;
        this.loadingTwitchChatbotDiagnostic = false;
      }
    });
  }

  private pickMostRecentActiveSessionForUser(sessions: SupportSession[], userId: string): SupportSession | null {
    return sessions
      .filter(session => session.status === 'ACTIVE' && session.targetUserId === userId)
      .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? ''))[0] ?? null;
  }

  private sortPermissions(permissions: string[]): string[] {
    return [...permissions].sort((left, right) => this.permissionSortKey(left) - this.permissionSortKey(right));
  }

  private permissionSortKey(permission: string): number {
    const categoryOrder = this.permissionCategoryOrder(permission);
    const rightOrder = permission.endsWith('_READ') ? 0 : 1;
    return categoryOrder * 10 + rightOrder;
  }

  private permissionCategoryOrder(permission: string): number {
    if (permission.startsWith('AUTO_MESSAGES')) return 0;
    if (permission.startsWith('BOT_COMMANDS')) return 1;
    if (permission.startsWith('DISCORD')) return 2;
    if (permission.startsWith('MODERATION')) return 3;
    if (permission.startsWith('TWITCH')) return 4;
    if (permission.startsWith('LEVELS')) return 5;
    return 99;
  }

  private ensureActiveIntegrationTab(): void {
    const available = this.integrationProviders.find(provider => this.integrationLinked(provider.key));
    this.activeIntegrationTab = (available?.key as 'DISCORD' | 'SPOTIFY' | 'YOUTUBE' | undefined) ?? 'DISCORD';
  }
}
